import { useEffect, useState, useCallback } from "react";

export default function SecurityOverlay({ visitorName, visitorEmail, children }) {
  const [blurred, setBlurred] = useState(false);

  const blockEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  useEffect(() => {
    // Block right-click globally
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Block keyboard shortcuts (Windows + Mac)
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const mod = isMac ? e.metaKey : e.ctrlKey;

      // Block: Ctrl/Cmd + S (save), P (print), U (view source)
      if (mod && (e.key === "s" || e.key === "S" || e.key === "p" || e.key === "P" || e.key === "u" || e.key === "U")) {
        e.preventDefault();
        return false;
      }
      // Block: Ctrl/Cmd + Shift + I (DevTools), Ctrl/Cmd + Shift + J (Console), Ctrl/Cmd + Shift + C (Inspector)
      if (mod && e.shiftKey && (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j" || e.key === "C" || e.key === "c")) {
        e.preventDefault();
        return false;
      }
      // Block F12 (DevTools)
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }
      // Block PrintScreen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        // Clear clipboard as an extra measure
        if (navigator.clipboard) {
          navigator.clipboard.writeText("").catch(() => {});
        }
        return false;
      }
      // Block Cmd+Shift+3 / Cmd+Shift+4 / Cmd+Shift+5 on Mac (screenshot shortcuts)
      if (isMac && e.metaKey && e.shiftKey && (e.key === "3" || e.key === "4" || e.key === "5")) {
        e.preventDefault();
        return false;
      }
    };

    // Block drag on all images
    const handleDragStart = (e) => {
      if (e.target.tagName === "IMG" || e.target.tagName === "VIDEO") {
        e.preventDefault();
        return false;
      }
    };

    // Visibility change — blur when tab is not active
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setBlurred(true);
      } else {
        // Small delay before unblurring to catch screenshot tool switches
        setTimeout(() => setBlurred(false), 300);
      }
    };

    // Window blur (catches Alt+Tab, app switching)
    const handleWindowBlur = () => setBlurred(true);
    const handleWindowFocus = () => setTimeout(() => setBlurred(false), 300);

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);

  const watermarkText = visitorName && visitorEmail
    ? `${visitorName} — ${visitorEmail}`
    : visitorEmail || "CONFIDENTIAL";

  return (
    <div
      className="relative"
      onContextMenu={blockEvent}
      onDragStart={blockEvent}
      style={{ WebkitUserSelect: "none", MozUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
    >
      {children}

      {/* Watermark Overlay — covers entire scrollable page */}
      <div
        className="fixed inset-0 z-[9990] pointer-events-none overflow-hidden"
        aria-hidden="true"
        data-testid="security-watermark"
      >
        <div className="absolute inset-0" style={{ width: "200vw", height: "200vh", top: "-20%", left: "-20%" }}>
          {Array.from({ length: 14 }).map((_, i) => (
            <div
              key={i}
              className="absolute whitespace-nowrap text-white font-serif"
              style={{
                transform: "rotate(-25deg)",
                top: `${i * 8}%`,
                left: "-5%",
                width: "130%",
                textAlign: "center",
                fontSize: "14px",
                letterSpacing: "0.5em",
                opacity: 0.025,
              }}
            >
              {watermarkText} &nbsp;&nbsp;&nbsp; {watermarkText} &nbsp;&nbsp;&nbsp; {watermarkText}
            </div>
          ))}
        </div>
      </div>

      {/* Blur overlay when tab loses focus */}
      {blurred && (
        <div
          className="fixed inset-0 z-[9998] backdrop-blur-xl bg-[#111110]/90 flex items-center justify-center"
          data-testid="security-blur-overlay"
        >
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#C5A059]/30 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#C5A059]/50">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <p className="text-[#C5A059]/60 text-xs uppercase tracking-[0.3em] mb-2">Content Protected</p>
            <p className="text-white/30 text-sm font-light">Return to this tab to continue viewing.</p>
          </div>
        </div>
      )}
    </div>
  );
}
